import PropTypes from "prop-types";
import { Button } from "components/global";
import Image from "next/image";
import { FaStar } from "react-icons/fa";
import { FaLocationDot, FaCheck } from "react-icons/fa6";
import { IoCallOutline, IoTimeOutline } from "react-icons/io5";
import { CiTimer } from "react-icons/ci";
import { SlCalender } from "react-icons/sl";
import { HiOutlineBadgeCheck } from "react-icons/hi";
import ForwardedLink from "components/global/forwarded-link";
import { useState, useRef, useEffect } from "react";

const LawyerCard = ({
  index,
  _id,
  avgRating,
  avatar,
  full_name,
  locations = [],
  topics = [],
  specializations = [],
  schedules = [],
  hideTopics = false,
  isOnline = false,
  responseTime = null,
  consultationsCount = 0,
  activeSpecialization,
}) => {
  const [imageError, setImageError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null); // 'specs' or 'topics'

  const getOnlineSchedule = () => schedules?.find(x => x?.type === 'online');
  const getOfficeSchedule = () => schedules?.find(x => x?.type === 'in-office');
  const hasOffer = schedules?.some(s => s.discount > 0);
  const maxDiscount = Math.max(...schedules.map(s => s.base_price ? s.discount / s.base_price : 0));

  const PriceDisplay = ({ schedule }) => {
    if (!schedule) {
      return <span>السعر غير محدد</span>;
    }

    const { base_price, discount } = schedule;
    if (discount > 0) {
      return (
        <span>
          <span className="absolute -top-1 -right-1 bg-red-100 text-red-800 border border-red-400 font-bold px-1 rounded-md">خصم {Math.round(discount / base_price * 100)}%</span>
          <span className="text-xs text-red-400 line-through">{base_price} ج</span> <span className="text-base">{base_price - discount} ج</span>
        </span>
      );
    }

    return <span className="font-bold">{base_price} ج</span>;
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }).map((_, i) => (
      <FaStar key={i} className={i < Math.round(rating || 0) ? "text-yellow-400" : "text-gray-300"} size={12} />
    ));

  // Handle tooltip toggle with better UX
  const handleTooltipToggle = (type) => {
    setShowTooltip(showTooltip === type ? null : type);
  };

  const tooltipRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTooltip]);

  const orderedSpecializations = [...specializations].sort((a, b) => {
    if (a._id === activeSpecialization) return -1;
    if (b._id === activeSpecialization) return 1;
    return 0;
  });


  return (
    <div className="group relative flex flex-col bg-white border border-gray-200 rounded-2xl w-full p-4 space-y-3 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden">
      {hasOffer && (
        <div className="corner-ribbon">
          <span className="text-xs">خصم حتى</span> <span className="text-md">{maxDiscount * 100}%</span>
        </div>
      )}

      {/* Online Status */}
      {isOnline && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
          متاح الآن
        </div>
      )}

      {/* Profile Section */}
      <div className="flex flex-col items-center">
        <div className="relative">
          <div className="relative w-24 h-24 mask-brand">
            <ForwardedLink href={`/customer/lawyers/${_id}`}>
              <Image
                alt={full_name || "محامي"}
                src={imageError ? "/tqneen/default-user.jpg" : (avatar || "/tqneen/default-user.jpg")}
                className="object-cover"
                width={96}
                height={96}
                quality={80}
                priority={index === 0}
                onError={() => setImageError(true)}
              />
            </ForwardedLink>
          </div>

          <div className="absolute top-0 -right-4 flex flex-col-reverse gap-y-2">
            {renderStars(avgRating)}
          </div>

          {/* {avgRating > 0 && (
            <div className="absolute -top-1 -right-1 bg-white rounded-full p-1.5 shadow-md border">
              <div className="flex items-center gap-1">
                <FaStar className="text-yellow-400" size={10} />
                <span className="text-xs font-semibold text-gray-700">{avgRating.toFixed(1)}</span>
              </div>
            </div>
          )} */}

          <div className="absolute bottom-2 right-2 bg-customer-primary rounded-full p-1 shadow-md">
            <HiOutlineBadgeCheck className="text-white" size={14} />
          </div>
        </div>

        {/* Name and Stats */}
        <div className="text-center mt-2 space-y-1">
          <ForwardedLink href={`/customer/lawyers/${_id}`}>
            <span className="inline-flex items-center justify-center gap-1 flex-row-reverse text-base font-bold text-gray-900 hover:text-customer-primary transition-colors cursor-pointer">
              <span>{full_name || "غير محدد"}</span>
              <span title="عرض الملف الشخصي">📄</span>
            </span>
          </ForwardedLink>

          <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
            {consultationsCount > 0 && (
              <div className="flex items-center gap-1">
                <FaCheck className="text-green-500" size={8} />
                <span>{consultationsCount} استشارة</span>
              </div>
            )}

            {responseTime && (
              <div className="flex items-center gap-1">
                <IoTimeOutline className="text-blue-500" size={10} />
                <span>يرد خلال {responseTime}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Section - Fixed Height */}
      <div className="flex-1 flex flex-col justify-between space-y-2">
        {/* Specializations */}
        <div className="relative flex flex-wrap gap-1 justify-center">
          {orderedSpecializations?.slice(0, 2)?.map((specialization, idx) => (
            <span
              key={idx}
              className={`text-xs/4 px-2 py-1 rounded-full font-medium line-clamp-1 ${specialization._id === activeSpecialization
                ? "bg-green-100 text-green-800 border border-green-300"
                : "bg-gradient-to-r from-customer-primary/10 to-customer-primary/5 text-customer-primary border border-customer-primary/20"
                }`}
            >
              {specialization?.name?.ar}
            </span>
          ))}
          {specializations?.length > 2 && (
            <div className="relative">
              <span role="button" tabIndex={0} onClick={() => handleTooltipToggle('specs')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTooltipToggle('specs'); } }}
                className="text-xs/3 px-1 py-1/2 rounded-full bg-gray-100 text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors select-none" >
                +{specializations.length - 2}
              </span>
            </div>
          )}
        </div>

        {/* Topics */}
        {!hideTopics && topics?.length > 0 && (
          <div className="relative flex flex-wrap gap-1 justify-center">
            {topics?.slice(0, 2)?.map((topic, idx) => (
              <div key={idx} className="flex items-center gap-1 text-gray-600">
                <span className="w-1 h-1 rounded-full bg-customer-primary/60"></span>
                <span className="text-xs/3 line-clamp-1">{topic?.name?.ar}</span>
              </div>
            ))}

            {topics?.length > 2 && (
              <div className="relative flex items-center gap-1 text-gray-600">
                <span className="w-1 h-1 rounded-full bg-gray-400"></span>
                <span role="button" tabIndex={0} onClick={() => handleTooltipToggle('topics')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTooltipToggle('topics'); } }}
                  className="text-xs/3 px-2 py-1/2 rounded-full bg-gray-50 text-gray-600 font-small cursor-pointer hover:bg-gray-200 transition-colors select-none" >
                  +{topics.length - 2}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Location */}
        {locations?.length > 0 && (
          <div className="flex items-center justify-center gap-1 text-gray-500 text-xs bg-gray-50 rounded-lg p-2">
            <FaLocationDot className="text-customer-primary/70 flex-shrink-0" size={12} />
            <span className="text-center line-clamp-1">
              {Array.isArray(locations[0]?.city)
                ? locations[0]?.city?.[0]?.name?.ar
                : locations[0]?.city?.name?.ar}
              {locations[0]?.area && (
                <>
                  {" - "}
                  {Array.isArray(locations[0]?.area)
                    ? locations[0]?.area?.[0]?.name?.ar
                    : locations[0]?.area?.name?.ar}
                </>
              )}
            </span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 pt-1">
          {getOnlineSchedule() && (<ForwardedLink href={`/customer/reservation?lawyer_id=${_id}&online=true`}>
            <Button className="w-full flex items-center justify-center gap-2 text-xs font-medium py-2.5 bg-gradient-to-r from-customer-primary to-customer-primary/90 hover:from-customer-primary/90 hover:to-customer-primary transform hover:scale-[1.02] transition-all duration-200">
              <CiTimer size={16} />
              <span>موعد أونلاين - <PriceDisplay schedule={getOnlineSchedule()} /></span>
            </Button>
          </ForwardedLink>)}

          {getOfficeSchedule() && (<ForwardedLink href={`/customer/reservation?lawyer_id=${_id}&online=false`}>
            <Button
              outline
              className="w-full flex items-center justify-center gap-2 text-xs font-medium py-2.5 border-2 border-customer-primary text-customer-primary hover:bg-customer-primary hover:text-white transform hover:scale-[1.02] transition-all duration-200 group"
            >
              <SlCalender size={14} className="group-hover:text-white" />
              <span>موعد مكتب - <PriceDisplay schedule={getOfficeSchedule()} /></span>
            </Button>
          </ForwardedLink>)}
        </div>
      </div>

      {/* Tooltips - Positioned outside card boundaries */}
      {showTooltip === 'specs' && specializations?.length > 2 && (
        <>
          {/* Backdrop to close tooltip */}
          <div aria-hidden="true" className="fixed inset-0 z-40" onClick={() => setShowTooltip(null)} />
          {/* Specializations Tooltip */}
          <div ref={tooltipRef} style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-2xl w-56 max-h-40 overflow-y-auto border border-gray-700" >
            <button onClick={() => setShowTooltip(null)} className="absolute top-1 right-2 text-white text-lg leading-none hover:text-red-400" aria-label="إغلاق"> ×</button>
            <div className="font-semibold text-center mb-2 text-yellow-400">التخصصات الأخرى:</div>
            <div className="space-y-1">
              {specializations?.slice(2)?.map((spec, idx) => (
                <div key={idx} className="text-center py-1.5 border-b border-gray-700 last:border-b-0 hover:bg-gray-800 rounded px-2 transition-colors">
                  {spec?.name?.ar}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {showTooltip === 'topics' && topics?.length > 2 && (
        <>
          {/* Backdrop to close tooltip */}
          <div aria-hidden="true" className="fixed inset-0 z-40" onClick={() => setShowTooltip(null)} />
          {/* Topics Tooltip */}
          <div ref={tooltipRef} style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-2xl w-56 max-h-40 overflow-y-auto border border-gray-700" >
            <button onClick={() => setShowTooltip(null)} className="absolute top-1 right-2 text-white text-lg leading-none hover:text-red-400" aria-label="إغلاق"> ×</button>
            <div className="font-semibold text-center mb-2 text-blue-400">المواضيع الأخرى:</div>
            <div className="space-y-1">
              {topics?.slice(2)?.map((topic, idx) => (
                <div key={idx} className="flex items-center gap-2 py-1.5 border-b border-gray-700 last:border-b-0 hover:bg-gray-800 rounded px-2 transition-colors">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></span>
                  <span>{topic?.name?.ar}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

LawyerCard.propTypes = {
  index: PropTypes.number,
  avgRating: PropTypes.number,
  avatar: PropTypes.string,
  _id: PropTypes.string.isRequired,
  full_name: PropTypes.string.isRequired,
  locations: PropTypes.array,
  topics: PropTypes.array,
  specializations: PropTypes.array,
  schedules: PropTypes.array,
  hideTopics: PropTypes.bool,
  isOnline: PropTypes.bool,
  responseTime: PropTypes.string,
  consultationsCount: PropTypes.number,
  activeSpecialization: PropTypes.string,
};

const LawyerCardSkeleton = () => (
  <div className="animate-pulse flex flex-col bg-white border border-gray-200 rounded-2xl w-full p-4 space-y-3">
    <div className="flex flex-col items-center space-y-2">
      <div className="w-24 h-24 bg-gray-200 mask-brand"></div>
      <div className="space-y-1 text-center">
        <div className="w-20 h-4 bg-gray-200 rounded mx-auto"></div>
        <div className="w-24 h-3 bg-gray-200 rounded mx-auto"></div>
      </div>
    </div>

    <div className="flex-1 flex flex-col justify-between space-y-2">
      <div className="flex gap-1 justify-center">
        <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
        <div className="w-18 h-6 bg-gray-200 rounded-full"></div>
      </div>
      <div className="w-28 h-6 bg-gray-200 rounded-lg mx-auto"></div>
      <div className="space-y-2">
        <div className="w-full h-10 bg-gray-200 rounded-lg"></div>
        <div className="w-full h-10 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  </div>
);

export { LawyerCard, LawyerCardSkeleton };